import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Minimize2 } from 'lucide-react';

import { cn } from '../../lib/utils';
import { CameraGrid } from './camera-grid';

interface FullCameraViewerProps {
  cameras: Array<{ id: string; name: string }>;
  selectedIds: string[];
  active: boolean;
  onToggle: (id: string) => void;
  onActivate: () => void;
  onDeactivate: () => void;
  className?: string;
  style?: React.CSSProperties;
  token?: string | null;
}

export function FullCameraViewer({
  cameras,
  selectedIds,
  active,
  onToggle,
  onActivate,
  onDeactivate,
  className,
  style,
  token,
}: FullCameraViewerProps) {
  const [fullscreen, setFullscreen] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [host] = useState(() => {
    const el = document.createElement('div');
    el.style.display = 'contents';
    return el;
  });

  useLayoutEffect(() => {
    (fullscreen ? document.body : slotRef.current)?.appendChild(host);
  }, [fullscreen, host]);
  useLayoutEffect(() => () => host.remove(), [host]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setFullscreen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const panel = (
    <div
      className={cn(
        'flex flex-col rounded-2xl border border-border bg-surface shadow-shell overflow-hidden min-h-0',
        fullscreen ? 'fixed inset-0 z-50 rounded-none border-0 shadow-none' : className,
      )}
      style={fullscreen ? undefined : style}
    >
      {/* Single-row header: title + pills + connect button. Pills cluster gets
          overflow-x-auto + min-w-0 so an overlong pill list scrolls
          horizontally rather than wrapping into a second row (which would
          push the stream area out of bounds). */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
        <span className="shrink-0 text-base font-semibold text-foreground">Camera Viewer</span>
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto scrollbar-none">
          {cameras.length === 0 ? (
            <span className="shrink-0 whitespace-nowrap rounded-md border-2 border-border bg-surface-muted px-4 py-2 text-sm font-semibold text-muted">
              No cameras available
            </span>
          ) : (
            cameras.map((camera) => {
              const selected = selectedIds.includes(camera.id);
              return (
                <button
                  key={camera.id}
                  onClick={() => onToggle(camera.id)}
                  className={cn(
                    'shrink-0 whitespace-nowrap rounded-md border-2 px-4 py-2 text-sm font-semibold transition-colors',
                    selected
                      ? 'border-accent bg-accent text-white shadow-sm'
                      : 'border-foreground/30 bg-foreground/[0.10] text-foreground hover:border-foreground/60 hover:bg-foreground/20',
                  )}
                  aria-pressed={selected}
                >
                  {camera.name}
                </button>
              );
            })
          )}
        </div>
        <button
          onClick={active ? onDeactivate : onActivate}
          disabled={cameras.length === 0}
          className={cn(
            'shrink-0 rounded-md border-2 px-5 py-2 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
            active
              ? 'border-foreground/30 bg-foreground/[0.12] text-foreground hover:bg-foreground/20'
              : 'border-accent bg-accent text-white hover:opacity-90',
          )}
        >
          {active ? 'Disconnect' : 'Connect'}
        </button>
        <button
          onClick={() => setFullscreen((v) => !v)}
          className="shrink-0 rounded-md p-2 text-muted transition-colors duration-150 hover:bg-surface-raised hover:text-foreground active:scale-95"
          aria-label={fullscreen ? 'Exit full screen' : 'Expand to full screen'}
          title={fullscreen ? 'Exit full screen (Esc)' : 'Expand to full screen'}
        >
          {fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </div>
      {active && (
        // CameraGrid now uses explicit auto-rows-[Npx] for the multi-tile
        // case, so the wrapper just needs flex-1 + min-h-0 — the grid sizes
        // itself via its tiles even when the parent has no computed height
        // (admin tabs use `space-y-6` containers with no height).
        <div className="flex-1 min-h-0 overflow-hidden p-4">
          <CameraGrid cameras={cameras} selectedIds={selectedIds} token={token} />
        </div>
      )}
    </div>
  );

  // The panel always renders into `host`, and full screen moves `host` to
  // <body>. A portal to a different container would remount the feeds and
  // reconnect every stream. The slot keeps the page layout while full screen.
  return (
    <>
      <div
        ref={slotRef}
        className={fullscreen ? className : undefined}
        style={fullscreen ? style : { display: 'contents' }}
        aria-hidden={fullscreen || undefined}
      />
      {createPortal(panel, host)}
    </>
  );
}
